import { useState, useEffect, useRef, useCallback } from 'react';
import type { QueryTuple, QueryLazyOptions } from '@apollo/react-hooks';
import { AbortError, DEFAULT_ABORT_ERROR_MESSAGE } from '@atlassian/jira-business-error-handling';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Request = () => any;
export type RequestQueueOptions = {
	isRequestLoading: boolean;
};

type AddRequestToAbortableQueueFunction = <TData, TVariables>(
	issueIds: number[],
	queryFunction: QueryTuple<TData, TVariables>[0],
	queryOptions: QueryLazyOptions<TVariables>,
) => void;

export type RequestQueueReturn = {
	addRequestToQueue: (request: Request) => void;
	addRequestToAbortableQueue: AddRequestToAbortableQueueFunction;
};

export const useRequestQueue = ({ isRequestLoading }: RequestQueueOptions): RequestQueueReturn => {
	const [abortableQueueMap, setAbortableQueueMap] = useState<Map<string, Request>>(new Map());
	const abortControllerMap = useRef<Map<string, AbortController>>(new Map());
	const inProgressRequestId = useRef<string | null>(null);

	const addRequestToAbortableQueue: AddRequestToAbortableQueueFunction = useCallback(
		(issueIds, queryFunction, queryOptions) => {
			const id = issueIds.join('-');

			const existingAbortController = abortControllerMap.current.get(id);
			existingAbortController?.abort(new AbortError(DEFAULT_ABORT_ERROR_MESSAGE));

			const abortController = new AbortController();
			abortControllerMap.current.set(id, abortController);

			const request = () => {
				queryFunction({
					...queryOptions,
					context: {
						...queryOptions.context,
						fetchOptions: {
							...queryOptions.context?.fetchOptions,
							signal: abortController.signal,
						},
					},
				});
			};

			setAbortableQueueMap((prevQueueMap) => new Map([...prevQueueMap, [id, request]]));
		},
		[],
	);

	useEffect(() => {
		if (!isRequestLoading && abortableQueueMap.size) {
			const iterator = abortableQueueMap.entries();
			const nextResult = iterator.next();

			if (!nextResult.done) {
				const [id, request] = nextResult.value;
				inProgressRequestId.current = id;
				request();
			}

			setAbortableQueueMap(new Map(iterator));
		}
	}, [abortableQueueMap, isRequestLoading]);

	const [queue, setQueue] = useState<Request[]>([]);

	useEffect(() => {
		if (!isRequestLoading && queue.length > 0) {
			setQueue((prevQueue) => {
				const [nextItem, ...restOfQueue] = prevQueue;
				nextItem();

				return restOfQueue;
			});
		}
	}, [queue, isRequestLoading]);

	const addRequestToQueue = (request: Request) => setQueue((prevQueue) => [...prevQueue, request]);

	return { addRequestToQueue, addRequestToAbortableQueue };
};
